import React, { ReactElement, useEffect } from 'react';
import './terminListTable.scss';
import {
  Button, Col,
  Text,
  Link, Row,
  Table,
  Tag,
  Tbody,
  Td,
  Tr,
  usePerPageValueFromUrl,
} from 'idsk-react-components';
import {
  Pagination,
  SortSelect,
  ConfirmModal,
  useResponsiveContext,
} from 'slovlex-idsk-react-components';
import { useParams } from 'react-router';
import { Termin } from 'tezaury-frontend-ts-axios';
import TerminListPage from '../../../../../../model/TerminListPage';
import { perPageDataArray } from '../../../../../../constants/PaginationPerPageConstants';
import routes from '../../../../../../routing/routes';
import { useClient, useClientStatusContext } from '../../../../../../context/ClientStatusContext';
import { changePublikacia } from '../../../../../../client/termin/terminPublikaciaChangeClient';
import TerminDeleteConfirmModal from '../../../../../DetailTerminu/component/TerminDeleteConfirmModal';
import getPaginationTotalLabel from '../../../../../../functions/getPaginationTotalLabel';
import { useSuccessMessageContext } from '../../../../../../context/SuccessMessageContext';
import { technickePricinyErrorStatus } from '../../../../../../constants/ErrorConstant';
import TerminTableMobileItem from '../terminTableMobileItem/TerminTableMobileItem';
import scrollToTop from '../../../../../../functions/scrollToTop';

interface Props {
  terminPage: TerminListPage | null | undefined;
  pageNumberFromUrl: number;
  reloadTerminPage;
  isFilterEmpty: boolean;
  onRecordsPerPageChange: (value: number) => void ;
}

const TerminListTable = ({
  terminPage,
  pageNumberFromUrl,
  reloadTerminPage,
  isFilterEmpty,
  onRecordsPerPageChange,
}: Props): ReactElement => {
  const { tezaurusUuid: uuid } = useParams<{ tezaurusUuid: string }>();
  const { perPageValue } = usePerPageValueFromUrl('perPage', perPageDataArray);
  const { displaySuccess } = useSuccessMessageContext();
  const { isSmallScreen } = useResponsiveContext();

  const {
    wrapClient: changePublikaciaClient,
    status: changePublikaciaStatus,
  } = useClient(changePublikacia);
  const { handleStatus } = useClientStatusContext();

  useEffect(() => {
    if (changePublikaciaStatus?.responseCode === 404) {
      handleStatus(technickePricinyErrorStatus);
    }
  }, [changePublikaciaStatus, handleStatus]);

  if (!terminPage) {
    return <></>;
  }

  const changePublikaciaButton = (
    termin: Termin,
    publikovany: boolean,
    openButtonText: string,
    confirmButtonStyle: 'action' | 'negative',
    confirmModalQuestion: string,
    confirmModalDescription: string,
  ): ReactElement => (
    <ConfirmModal
      question={confirmModalQuestion}
      description={confirmModalDescription}
      cancelButtonText="Zrušiť"
      confirmButton={
          (
            <Button buttonStyle={confirmButtonStyle} size="large">{openButtonText}</Button>
          )
        }
      openButton={(
        <Button
          buttonStyle="link"
          size="small"
        >
          {openButtonText}
        </Button>
        )}
      doAfterConfirm={async () => {
        await changePublikaciaClient(termin.uuid, publikovany);
        reloadTerminPage();
        displaySuccess('Vaša požiadavka bola úspešne vykonaná.');
        scrollToTop();
      }}
    />
  );

  return (
    <div>
      <div className="termin-list-table">
        {isSmallScreen && (
        <Row className="termin-list-mobile-sort-select-row">
          <Col>
            <SortSelect
              sortList={[
                { sort: 'preferovanyTerminSort', sortBy: 'asc', title: 'Termín (A - Z)' },
                { sort: 'preferovanyTerminSort', sortBy: 'desc', title: 'Termín (Z - A)' },
              ]}
            />
          </Col>
        </Row>
        )}
        <div className="total-sort-container">
          <Row className="pagination-label-sort-row">
            <Col
              widthDesktop="one-third"
              width="full"
              className={isSmallScreen && 'pagination-label-mobile'}
            >
              <Text className="total-label">{getPaginationTotalLabel(terminPage, pageNumberFromUrl, 'Termíny')}</Text>
            </Col>
            {!isSmallScreen && (
            <Col width="full" widthDesktop="two-thirds">
              <SortSelect
                sortList={[
                  { sort: 'preferovanyTerminSort', sortBy: 'asc', title: 'Termín (A - Z)' },
                  { sort: 'preferovanyTerminSort', sortBy: 'desc', title: 'Termín (Z - A)' },
                ]}
              />
            </Col>
            )}
          </Row>
        </div>
        {terminPage.totalElements === 0 ? (
          emptyListElement({ terminPage, isEmptyFilter: isFilterEmpty })
        ) : (
          <div>
            {isSmallScreen ? (
              <div className="mobile-termin-list-table">
                {terminPage.terminList.map((termin) => (
                  <TerminTableMobileItem
                    key={termin.uuid}
                    termin={termin.termin}
                    stavPublikacie={termin.publikovany}
                    uuid={uuid}
                    terminUuid={termin.uuid}
                    actions={(
                      <>
                        {termin.publikovany
                          ? (
                            changePublikaciaButton(
                              termin,
                              false,
                              'Zrušiť publikovanie',
                              'negative',
                              `Naozaj si prajete zrušiť publikovanie termínu "${termin.termin}"?`,
                              'Po zrušení publikovania už termín nebude dostupný pre verejnosť.',
                            )
                          )
                          : (
                            changePublikaciaButton(
                              termin,
                              true,
                              'Publikovať',
                              'action',
                              `Naozaj si prajete publikovať termín "${termin.termin}"?`,
                              'Po publikovaní bude termín dostupný pre verejnosť.',
                            )
                          )}
                                  &emsp;|&emsp;
                        <TerminDeleteConfirmModal
                          termin={termin}
                          buttonStyle="link-negative"
                          buttonSize="small"
                          openButtonText="Zmazať"
                          reloadTerminPage={reloadTerminPage}
                        />
                      </>
                            )}
                  />
                ))}
              </div>
            ) : (
              <Table>
                <Tbody>
                  <tr className="govuk-table__cell" />
                  {terminPage.terminList.map((termin) => (
                    <Tr key={termin.uuid}>
                      <Td>
                        <Link to={routes.tezaurus.admin.termin.detail.withParams(uuid, termin.uuid)}>
                          {termin.termin}
                        </Link>
                        {!termin.publikovany && (
                        <Tag className="m-l-10" size="medium" type="danger">nepublikované</Tag>
                        )}
                      </Td>
                      <Td>
                        {termin.publikovany
                          ? (
                            changePublikaciaButton(
                              termin,
                              false,
                              'Zrušiť publikovanie',
                              'negative',
                              `Naozaj si prajete zrušiť publikovanie termínu "${termin.termin}"?`,
                              'Po zrušení publikovania už termín nebude dostupný pre verejnosť.',
                            )
                          )
                          : (
                            changePublikaciaButton(
                              termin,
                              true,
                              'Publikovať',
                              'action',
                              `Naozaj si prajete publikovať termín "${termin.termin}"?`,
                              'Po publikovaní bude termín dostupný pre verejnosť.',
                            )
                          )}
                            &emsp;|&emsp;
                        <TerminDeleteConfirmModal
                          termin={termin}
                          buttonStyle="link-negative"
                          buttonSize="small"
                          openButtonText="Zmazať"
                          reloadTerminPage={reloadTerminPage}
                        />
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            )}
          </div>
        )}
        <Pagination
          number={pageNumberFromUrl}
          size={terminPage.size}
          totalElements={terminPage.totalElements}
          onRecordsPerPageChange={onRecordsPerPageChange}
          recordsPerPage={perPageDataArray}
          perPageValue={perPageValue}
          hideTotal
        />
      </div>
    </div>
  );
};

interface EmptyElementProps {
  terminPage: TerminListPage,
  isEmptyFilter: boolean,
}

const emptyListElement = ({ terminPage, isEmptyFilter }: EmptyElementProps): ReactElement => {
  if (!isEmptyFilter) {
    return (
      <Text type="p" size="l" className="text-center">Nenašli sa žiadne termíny zodpovedajúce zvolenému filtru.</Text>
    );
  }
  return (<p className="text-center"> {terminPage.tezaurus} nemá žiadne termíny.</p>);
};

export default TerminListTable;
